import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    CheckBox,
    DataTable,
    FormField,
    Layer,
    Select,
    Text,
    TextInput,
} from "grommet";
import { Add, Search } from "grommet-icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context";
import { CurrencyInput } from "../../components";
import { formatCents, parseDollarsToCents } from "../../utils/money";
import { createLoan, getLoans } from "../../api/funderAllocation";
import { Panel } from "./Panel";
import { ctaButtonProps } from "./ctaButton";
import styled from "styled-components";

const LOAN_STATUSES = ["Settled", "Discharged"];

// Equal-width digits so numbers of different lengths still line up in a
// right-aligned numeric column, without switching the whole cell to a
// monospace font.
const tabularNums = { fontVariantNumeric: "tabular-nums" };

const formatActiveFunders = (names) => {
    if (!names || names.length === 0) return "--";
    return names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0];
};

const DEFAULT_SORT = { property: "ref", direction: "desc" };

const hasNoFunderAllocation = (loan) =>
    !loan.activeFunderNames || loan.activeFunderNames.length === 0;

// Mirrors DataTable's own internal comparator (buildState.filterAndSortData)
// so that sorting within each pinned/unpinned group matches what clicking a
// column header would otherwise do.
const compareLoans = (a, b, { property, direction }) => {
    const aVal = a[property];
    const bVal = b[property];
    const sortAsc = direction === "asc";
    const before = sortAsc ? 1 : -1;
    const after = sortAsc ? -1 : 1;
    if (
        (typeof aVal === "string" && typeof bVal === "string") ||
        (typeof aVal === "string" && !bVal) ||
        (typeof bVal === "string" && !aVal)
    ) {
        const result = (aVal || "").localeCompare(bVal || "", undefined, {
            sensitivity: "base",
        });
        return sortAsc ? result : -result;
    }
    if (aVal > bVal) return before;
    if (aVal < bVal) return after;
    return 0;
};

// DataTable spreads unknown props (including the className styled() adds)
// onto its own <table> element, so this rule has to target the table
// itself via `&`. A nested `table { ... }` would compile to `.cls table`,
// a *descendant* selector that never matches, leaving the browser's
// default table-layout:auto in charge - which sizes columns from content
// first (so a long Name widens the table past its container and `truncate`
// never kicks in) and treats our percentage `size`s as mere hints. `&&`
// doubles the class to out-rank Grommet's own `width: inherit`.
const FixedTable = styled(DataTable)`
    && {
        table-layout: fixed;
        width: 100%;
    }
`;

const LoanListSection = () => {
    const { tokens } = useAuth();
    const navigate = useNavigate();

    const [loans, setLoans] = useState([]);
    const [showDeleted, setShowDeleted] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [sort, setSort] = useState(DEFAULT_SORT);

    const [showCreate, setShowCreate] = useState(false);
    const [createValue, setCreateValue] = useState({
        ref: "",
        name: "",
        amount: "",
        status: "Settled",
    });
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        if (!tokens?.idToken) return;
        setLoading(true);
        setError(null);
        try {
            const data = await getLoans(tokens.idToken, { showDeleted });
            setLoans(data || []);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to load loans");
        } finally {
            setLoading(false);
        }
    }, [tokens, showDeleted]);

    useEffect(() => {
        load();
    }, [load]);

    const filteredLoans = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return loans;
        return loans.filter(
            (loan) =>
                loan.ref?.toLowerCase().includes(q) ||
                loan.name?.toLowerCase().includes(q),
        );
    }, [loans, searchQuery]);

    // Pin loans with no active funder allocation above the rest, so they're
    // immediately visible, while still sorting within each group by
    // whatever column/direction is currently active (ref desc by default).
    const { rows: sortedLoans, dividerRef } = useMemo(() => {
        const unallocated = [];
        const allocated = [];
        filteredLoans.forEach((loan) => {
            (hasNoFunderAllocation(loan) ? unallocated : allocated).push(loan);
        });
        const byActiveSort = (a, b) => compareLoans(a, b, sort);
        unallocated.sort(byActiveSort);
        allocated.sort(byActiveSort);
        return {
            rows: [...unallocated, ...allocated],
            dividerRef:
                unallocated.length > 0 && allocated.length > 0
                    ? allocated[0].ref
                    : undefined,
        };
    }, [filteredLoans, sort]);

    const amountCents = parseDollarsToCents(createValue.amount);
    const canCreate = createValue.ref && createValue.name && amountCents > 0;

    const handleCreate = async () => {
        if (!canCreate) return;
        setSaving(true);
        setError(null);
        try {
            const created = await createLoan(tokens.idToken, {
                ref: createValue.ref,
                name: createValue.name,
                amountCents,
                status: createValue.status,
            });
            setShowCreate(false);
            setCreateValue({
                ref: "",
                name: "",
                amount: "",
                status: "Settled",
            });
            navigate(`/funder-allocation/loans/${created.id}`);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to create loan");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box gap="medium">
            <Box direction="row" justify="between" align="center" gap="medium">
                <CheckBox
                    checked={showDeleted}
                    label="Show deleted"
                    onChange={(e) => setShowDeleted(e.target.checked)}
                />
                <Box flex align="center">
                    <TextInput
                        icon={<Search />}
                        placeholder="Search by reference or name"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        width="medium"
                    />
                </Box>
                <Button
                    icon={<Add />}
                    label="New loan"
                    onClick={() => setShowCreate(true)}
                    {...ctaButtonProps}
                />
            </Box>
            {error && <Text color="status-critical">{error}</Text>}
            <Panel>
                {loading ? (
                    <Text>Loading…</Text>
                ) : loans.length === 0 ? (
                    <Text color="dark-5">No loans found</Text>
                ) : filteredLoans.length === 0 ? (
                    <Text color="dark-5">No loans match your search</Text>
                ) : (
                    <FixedTable
                        columns={[
                            {
                                property: "ref",
                                header: "Reference",
                                render: (d) => (
                                    <Text style={tabularNums}>{d.ref}</Text>
                                ),
                                size: "14%",
                            },
                            {
                                property: "name",
                                header: "Name",
                                render: (d) => (
                                    <Text truncate title={d.name}>
                                        {d.name}
                                    </Text>
                                ),
                                size: "47%",
                            },
                            {
                                property: "amountCents",
                                header: "Amount",
                                align: "end",
                                render: (d) => (
                                    <Text style={tabularNums}>
                                        {formatCents(d.amountCents)}
                                    </Text>
                                ),
                                size: "15%",
                            },
                            {
                                property: "status",
                                header: "Status",
                                size: "12%",
                            },
                            {
                                property: "activeFunderNames",
                                header: "Funder",
                                render: (d) =>
                                    formatActiveFunders(d.activeFunderNames),
                                size: "12%",
                            },
                        ]}
                        data={sortedLoans}
                        onClickRow={({ datum }) =>
                            navigate(`/funder-allocation/loans/${datum.id}`)
                        }
                        sort={sort}
                        onUpdate={(opts) => opts.sort && setSort(opts.sort)}
                        sortable
                        rowProps={
                            dividerRef
                                ? {
                                      [dividerRef]: {
                                          border: {
                                              side: "top",
                                              size: "small",
                                              color: "border",
                                          },
                                      },
                                  }
                                : undefined
                        }
                        fill="horizontal"
                    />
                )}
            </Panel>

            {showCreate && (
                <Layer
                    onEsc={() => setShowCreate(false)}
                    onClickOutside={() => setShowCreate(false)}
                >
                    <Box pad="medium" gap="small" width="medium">
                        <Text weight="bold">New loan</Text>
                        <FormField label="Reference" required>
                            <TextInput
                                value={createValue.ref}
                                onChange={(e) =>
                                    setCreateValue((v) => ({
                                        ...v,
                                        ref: e.target.value,
                                    }))
                                }
                            />
                        </FormField>
                        <FormField label="Name" required>
                            <TextInput
                                value={createValue.name}
                                onChange={(e) =>
                                    setCreateValue((v) => ({
                                        ...v,
                                        name: e.target.value,
                                    }))
                                }
                            />
                        </FormField>
                        <FormField label="Amount" required>
                            <CurrencyInput
                                value={createValue.amount}
                                onChange={(raw) =>
                                    setCreateValue((v) => ({
                                        ...v,
                                        amount: raw,
                                    }))
                                }
                            />
                        </FormField>
                        <FormField label="Status">
                            <Select
                                options={LOAN_STATUSES}
                                value={createValue.status}
                                onChange={({ option }) =>
                                    setCreateValue((v) => ({
                                        ...v,
                                        status: option,
                                    }))
                                }
                            />
                        </FormField>
                        <Box
                            direction="row"
                            gap="small"
                            justify="end"
                            margin={{ top: "small" }}
                        >
                            <Button
                                label="Cancel"
                                onClick={() => setShowCreate(false)}
                            />
                            <Button
                                label="Create"
                                {...ctaButtonProps}
                                disabled={!canCreate || saving}
                                onClick={handleCreate}
                            />
                        </Box>
                    </Box>
                </Layer>
            )}
        </Box>
    );
};

export { LoanListSection };
